import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { CustomerAddress } from '../../customer/model/customer-information.model';
import { AddressViewService } from '../../customer/services/address.view-service';
import { TolerantPostRequestModelMapper } from './mapper/request-model.mapper';
import { TolerantPostViewModelMapper } from './mapper/view-model.mapper';
import { TolerantPostFCCityStreetNumberQueryOptions } from './model/request.model';
import { SuggestedAddresses } from './model/view.model';
import { TolerantPostClient } from './tolerant-post.client';

const TOLERANT_MAX_RESULTS_COUNT = 10;

@Injectable({
  providedIn: 'root',
})
export class TolerantPostService {
  private readonly client = inject(TolerantPostClient);
  private readonly viewMapper = inject(TolerantPostViewModelMapper);
  private readonly requestMapper = inject(TolerantPostRequestModelMapper);
  private readonly addressViewService = inject(AddressViewService);
  private readonly logger = inject(NGXLogger);

  private async resolveAddress(
    searchParams: TolerantPostFCCityStreetNumberQueryOptions,
    interactive: boolean,
  ): Promise<SuggestedAddresses> {
    searchParams.reqId = searchParams.reqId ? searchParams.reqId : crypto.randomUUID();
    searchParams.maxResultCount = searchParams.maxResultCount ?? TOLERANT_MAX_RESULTS_COUNT;
    this.logger.log(`#CDFTF0018 Sending request to tolerant using reqId ${searchParams.reqId}`);

    const { responseStatus, responseBody } = await this.client.resolveAddress(searchParams, interactive);

    if (responseStatus < 200 || responseStatus > 299 || (responseBody?.returnCode ?? 0) !== 0) {
      throw new Error(
        `#CDFTF0014 Unable to fetch for addresses. Used reqId: ${searchParams.reqId}. Error: ${responseBody?.errorMsg}`,
      );
    }

    return this.viewMapper.mapResponse(responseBody);
  }

  // Is used to fetch a list of suggestions. Is NOT used to validate if an address is valid
  async resolveAddressFromSingleLineQuery(singleLineQuery: string, country: string) {
    // Setting country individually is mandatory for receiving results

    return await this.resolveAddress(
      {
        country,
        singleLine: singleLineQuery,
      },
      false,
    );
  }

  // Is used to fetch a list of suggestions. Is NOT used to validate if an address is valid.
  async resolveAddressFromCustomerAddress(address: CustomerAddress) {
    // Do not use province, as the user is unable to set the value itself. It is set via the final (successful) tolerant validation.
    const addressWithoutState = { ...address };
    delete addressWithoutState.province;
    const singleLineQuery = this.addressViewService.getAsSingleLineString(addressWithoutState);

    const country = address.country;
    if (!country) {
      throw new Error(
        '#CDFTF0023 Customer has no country information. Country information is required to query Tolerant Post.',
      );
    }

    return this.resolveAddressFromSingleLineQuery(singleLineQuery, country);
  }

  // Is used to validate if an address is valid. Is NOT used to fetch a list of suggestions.
  async resolveAddressInteractive(address: CustomerAddress) {
    return await this.resolveAddress(
      {
        ...this.requestMapper.map(address),
        // Do not send province, as the user is unable to set the value itself. It is set via the final (successful) tolerant validation.
        province: '',
      },
      true,
    );
  }
}
